export interface DeviceKeyRecord {
  deviceId: string;
  publicKey: Uint8Array;
  privateKey: Uint8Array; // Only stored locally, never sent to server
}

const DB_NAME = 'lvdr-crypto';
const STORE_NAME = 'device-keys';
// COS-12: per-workflow background-execution tokens (bgToken), each WRAPPED to
// this device's X25519 public key before it is stored. Lives in the same
// device-tied `lvdr-crypto` DB — never a session-tied store — and it is destroyed
// with the device key on erasure. The wrap is NOT protection against a copy of this
// DB: the X25519 private key is raw bytes in `device-keys`, so a whole-DB copy
// unwraps every row. The wake tokens live in `lvdr-wake` under a non-extractable
// key, so reading those needs this browser. Keyed out-of-line by
// `${userId}::${workflowId}`.
const BG_TOKEN_STORE = 'bg-tokens';

// DB version bumped 1 → 2 to add BG_TOKEN_STORE. openDB is the single owner of
// the version for this DB name, so every caller opens at v2 and existing v1
// clients run the guarded upgrade below exactly once.
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      // Guarded creates: a v1→v2 upgrade already has `device-keys`, and an
      // unconditional createObjectStore on an existing store throws.
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      if (!db.objectStoreNames.contains(BG_TOKEN_STORE)) {
        db.createObjectStore(BG_TOKEN_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function bgTokenKey(userId: string, workflowId: string): string {
  return `${userId}::${workflowId}`;
}

export async function saveDeviceKey(userId: string, record: DeviceKeyRecord): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(record, userId);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

export async function getDeviceKey(userId: string): Promise<DeviceKeyRecord | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).get(userId);
    request.onsuccess = () => {
      db.close();
      resolve(request.result ?? null);
    };
    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

/** Call after a successful crypto unlock so future page loads know to wait for the device key. */
export function markUserEncrypted(userId: string): void {
  try {
    localStorage.setItem(`lvdr-user-encrypted:${userId}`, '1');
  } catch {
    // Private browsing / storage quota — ignore
  }
}

/** True if this user has previously completed a crypto unlock on this browser. */
export function isUserMarkedEncrypted(userId: string): boolean {
  try {
    return localStorage.getItem(`lvdr-user-encrypted:${userId}`) === '1';
  } catch {
    return false;
  }
}

/** Remove the encrypted marker for this user (e.g. after device revocation). */
export function clearUserEncrypted(userId: string): void {
  try {
    localStorage.removeItem(`lvdr-user-encrypted:${userId}`);
  } catch {
    // Private browsing / storage quota — ignore
  }
}

/**
 * Polls IndexedDB for the device key until it appears or the timeout expires.
 * Use on first WS connect when isUserMarkedEncrypted() is true — the key may
 * still be committing from the bootstrap wizard when the socket opens.
 *
 * 1.5s is plenty for the bootstrap race; if the key still isn't there, it has
 * been deleted (or the flag is stale) and the caller should fall through to
 * the enrollment flow rather than blocking the user further.
 */
export async function waitForDeviceKey(userId: string, timeoutMs = 1500): Promise<DeviceKeyRecord | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const key = await getDeviceKey(userId);
    if (key) return key;
    await new Promise((r) => setTimeout(r, 200));
  }
  return null;
}

export async function deleteDeviceKey(userId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(userId);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

/**
 * saveBgToken stores a background-execution token for one workflow, already
 * WRAPPED to this device's public key (see `wrapMasterKEK`). The caller must
 * never persist the raw token — only the wrapped blob lands here.
 */
export async function saveBgToken(
  userId: string,
  workflowId: string,
  wrapped: Uint8Array,
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BG_TOKEN_STORE, 'readwrite');
    tx.objectStore(BG_TOKEN_STORE).put(wrapped, bgTokenKey(userId, workflowId));
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

/** getBgToken returns the wrapped bgToken for a workflow, or null if none. */
export async function getBgToken(userId: string, workflowId: string): Promise<Uint8Array | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BG_TOKEN_STORE, 'readonly');
    const request = tx.objectStore(BG_TOKEN_STORE).get(bgTokenKey(userId, workflowId));
    request.onsuccess = () => {
      db.close();
      resolve((request.result as Uint8Array | undefined) ?? null);
    };
    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

/** deleteBgToken removes one workflow's wrapped bgToken (revoke-one). */
export async function deleteBgToken(userId: string, workflowId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BG_TOKEN_STORE, 'readwrite');
    tx.objectStore(BG_TOKEN_STORE).delete(bgTokenKey(userId, workflowId));
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

/**
 * deleteAllBgTokens removes every wrapped bgToken for a user (revoke-all and
 * delete-account; the account kill switch keeps them). Keys are `${userId}::${workflowId}`, so the whole
 * user-scoped range is dropped in one bound delete.
 */
export async function deleteAllBgTokens(userId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BG_TOKEN_STORE, 'readwrite');
    // '￿' is above any workflowId char, so the bound covers this user only.
    const range = IDBKeyRange.bound(`${userId}::`, `${userId}::￿`);
    tx.objectStore(BG_TOKEN_STORE).delete(range);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

function deleteLegacyRecord(): void {
  // Clean up old un-scoped localStorage flag
  try { localStorage.removeItem('lvdr-user-encrypted'); } catch { /* ignore */ }

  // Clean up old un-scoped IDB record
  openDB().then((db) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete('current');
    tx.oncomplete = () => db.close();
    tx.onerror = () => db.close();
  }).catch(() => { /* ignore — DB may not exist yet */ });
}

deleteLegacyRecord();
